import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Newspaper, Image as ImageIcon, Search, BookOpen, Send,
  Sparkles, Edit3, ListOrdered, Terminal, Copy, Check, Trash2,
  Loader2, ChevronDown, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

// prompt_type → icon + friendly label. Kept in sync with the set in
// backend/prompt_history.py PROMPT_TYPES.
const TYPE_META = {
  research: {
    icon: Search,
    get label() { return tr("Story ideas"); },
    tone: "text-indigo-700 bg-indigo-50 border-indigo-100",
  },
  article: {
    icon: Newspaper,
    get label() { return tr("Article"); },
    tone: "text-slate-800 bg-slate-100 border-slate-200",
  },
  article_revise: {
    icon: Edit3,
    get label() { return tr("Revise"); },
    tone: "text-amber-800 bg-amber-50 border-amber-100",
  },
  custom_article: {
    icon: Newspaper,
    get label() { return tr("Custom article"); },
    tone: "text-slate-800 bg-slate-100 border-slate-200",
  },
  find_sources: {
    icon: BookOpen,
    get label() { return tr("Find sources"); },
    tone: "text-emerald-800 bg-emerald-50 border-emerald-100",
  },
  rank_sources: {
    icon: ListOrdered,
    get label() { return tr("Rank sources"); },
    tone: "text-teal-800 bg-teal-50 border-teal-100",
  },
  telegram_tip: {
    icon: Send,
    get label() { return tr("Telegram tip"); },
    tone: "text-sky-800 bg-sky-50 border-sky-100",
  },
  image: {
    icon: ImageIcon,
    get label() { return tr("Image"); },
    tone: "text-fuchsia-800 bg-fuchsia-50 border-fuchsia-100",
  },
  ad_banner: {
    icon: ImageIcon,
    get label() { return tr("Sponsor banner"); },
    tone: "text-amber-800 bg-amber-50 border-amber-100",
  },
  generic: {
    icon: Sparkles,
    get label() { return tr("Generic"); },
    tone: "text-neutral-700 bg-neutral-100 border-neutral-200",
  },
};

const FILTERS = [
  { key: "all", get label() { return tr("All"); } },
  { key: "failures", get label() { return tr("Failures"); } },
  { key: "research", get label() { return tr("Story ideas"); } },
  { key: "article", get label() { return tr("Article"); } },
  { key: "article_revise", get label() { return tr("Revise"); } },
  { key: "find_sources", get label() { return tr("Find sources"); } },
  { key: "rank_sources", get label() { return tr("Rank sources"); } },
  { key: "image", get label() { return tr("Image"); } },
];

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return tr("just now");
  if (diff < 3600) return tr("{floor}m ago", { floor: Math.floor(diff / 60) });
  if (diff < 86400) return tr("{floor}h ago", { floor: Math.floor(diff / 3600) });
  return d.toLocaleString(uiLocale());
}

function PromptRow({ item }) {
  const meta = TYPE_META[item.prompt_type] || TYPE_META.generic;
  const Icon = meta.icon;
  const [expanded, setExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const combined = useMemo(() => {
    const parts = [];
    if (item.system_prompt) {
      parts.push("=== SYSTEM PROMPT ===\n" + item.system_prompt);
    }
    parts.push("=== USER PROMPT ===\n" + (item.user_prompt || ""));
    return parts.join("\n\n");
  }, [item]);

  const copy = async (which, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(which);
      setTimeout(() => setCopiedField(null), 1600);
      toast.success(tr("{value} copied", { value: which === "combined" ? tr("Full prompt") : which }));
    } catch {
      toast.error(tr("Copy failed — your browser may block clipboard access"));
    }
  };

  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => api.deletePrompt(item.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts"] });
      toast.success(tr("Prompt deleted"));
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Delete failed")),
  });

  return (
    <li
      data-testid={`prompt-row-${item.id}`}
      className="border-b border-[var(--border)] bg-white"
    >
      <div className="flex items-start gap-3 p-4">
        <div
          className={
            "shrink-0 mt-0.5 w-8 h-8 grid place-items-center border " +
            meta.tone
          }
          title={meta.label}
        >
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span
              className={
                "inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 border " +
                meta.tone
              }
            >
              {meta.label}
            </span>
            {item.failure && (
              <span
                data-testid={`prompt-failed-badge-${item.id}`}
                title={item.failure.reason}
                className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-red-300 bg-red-50 text-red-800"
              >{tr("Failed")}</span>
            )}
            <span className="text-sm font-medium truncate min-w-0">
              {item.label || tr("(no label)")}
            </span>
            <span
              className="text-xs text-[var(--ink-muted)] shrink-0"
              title={item.created_at}
            >
              {relativeTime(item.created_at)}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-[var(--ink-muted)] font-mono-display truncate">
            {item.model}
          </div>

          {!expanded && (
            <p className="mt-2 text-sm text-[var(--ink-muted)] line-clamp-2 whitespace-pre-wrap break-words">
              {(item.user_prompt || "").slice(0, 500)}
            </p>
          )}

          {expanded && (
            <div className="mt-3 space-y-3">
              {item.system_prompt && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    {trRich("<0>System prompt</0>", null, [<span className="overline" />])}
                    <button
                      data-testid={`prompt-copy-system-${item.id}`}
                      onClick={() => copy(tr("System"), item.system_prompt)}
                      className="text-xs inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                    >
                      {copiedField === tr("System") ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}{tr("Copy")}</button>
                  </div>
                  <pre className="text-xs whitespace-pre-wrap break-words border border-[var(--border)] bg-neutral-50 p-3 max-h-72 overflow-y-auto">
                    {item.system_prompt}
                  </pre>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  {trRich("<0>User prompt</0>", null, [<span className="overline" />])}
                  <button
                    data-testid={`prompt-copy-user-${item.id}`}
                    onClick={() => copy(tr("User"), item.user_prompt)}
                    className="text-xs inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                  >
                    {copiedField === tr("User") ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}{tr("Copy")}</button>
                </div>
                <pre className="text-xs whitespace-pre-wrap break-words border border-[var(--border)] bg-neutral-50 p-3 max-h-96 overflow-y-auto">
                  {item.user_prompt || tr("(empty)")}
                </pre>
              </div>
              {item.failure && (
                <div
                  data-testid={`prompt-failure-${item.id}`}
                  className="border-t border-red-200 pt-3 mt-3"
                >
                  {/* The `failure` sub-document is written by
                      backend/prompt_history.log_failure whenever an
                      LLM generation raises after Claude returned
                      unparseable JSON. Excerpts are truncated at
                      FAILURE_EXCERPT_CHARS (4000) each end. */}
                  <div className="flex items-center justify-between mb-1">
                    {trRich("<0>Response (failed)</0>", null, [<span className="overline text-red-700" />])}
                    <button
                      data-testid={`prompt-copy-failure-${item.id}`}
                      onClick={() =>
                        copy(
                          tr("Failure"),
                          [
                            "reason: " + (item.failure.reason || ""),
                            "stop_reason: " + (item.failure.stop_reason || ""),
                            "response_length: " + (item.failure.response_length ?? ""),
                            "parsed_keys: " + JSON.stringify(item.failure.parsed_keys || []),
                            "",
                            "=== response_head ===",
                            item.failure.response_head || "",
                            "",
                            "=== response_tail ===",
                            item.failure.response_tail || "",
                          ].join("\n"),
                        )
                      }
                      className="text-xs inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                    >
                      {copiedField === tr("Failure") ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}{tr("Copy")}</button>
                  </div>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs mb-2">
                    <dt className="text-[var(--ink-muted)]">{tr("Reason")}</dt>
                    <dd className="text-red-800 font-medium">
                      {item.failure.reason || tr("(unknown)")}
                    </dd>
                    <dt className="text-[var(--ink-muted)]">{tr("Stop reason")}</dt>
                    <dd className="font-mono-display">
                      {item.failure.stop_reason || "—"}
                    </dd>
                    <dt className="text-[var(--ink-muted)]">{tr("Response length")}</dt>
                    <dd className="font-mono-display">
                      {tr("{response_length} chars", { response_length: item.failure.response_length ?? 0 })}</dd>
                    <dt className="text-[var(--ink-muted)]">{tr("Parsed keys")}</dt>
                    <dd className="font-mono-display break-words">
                      {(item.failure.parsed_keys || []).length
                        ? JSON.stringify(item.failure.parsed_keys)
                        : "—"}
                    </dd>
                  </dl>
                  {item.failure.response_head && (
                    <div className="mb-2">
                      <div className="overline mb-1">{tr("Response head (first {response_headCount} chars)", { response_headCount: item.failure.response_head.length })}</div>
                      <pre className="text-xs whitespace-pre-wrap break-words border border-red-200 bg-red-50 p-3 max-h-72 overflow-y-auto">
                        {item.failure.response_head}
                      </pre>
                    </div>
                  )}
                  {item.failure.response_tail && (
                    <div>
                      <div className="overline mb-1">{tr("Response tail (last {response_tailCount} chars)", { response_tailCount: item.failure.response_tail.length })}</div>
                      <pre className="text-xs whitespace-pre-wrap break-words border border-red-200 bg-red-50 p-3 max-h-72 overflow-y-auto">
                        {item.failure.response_tail}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              data-testid={`prompt-toggle-${item.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)]"
            >
              {expanded ? (
                <>
                  <ChevronDown className="w-3 h-3" />{tr("Collapse")}</>
              ) : (
                <>
                  <ChevronRight className="w-3 h-3" />{tr("Expand")}</>
              )}
            </button>
            <button
              data-testid={`prompt-copy-combined-${item.id}`}
              onClick={() => copy("combined", combined)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--slate-900)] bg-[var(--slate-900)] text-white hover:bg-black"
            >
              {copiedField === "combined" ? (
                <Check className="w-3 h-3" />
              ) : (
                <Copy className="w-3 h-3" />
              )}{tr("Copy full prompt")}</button>
            <button
              data-testid={`prompt-delete-${item.id}`}
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-transparent hover:border-red-200 hover:bg-red-50 text-[var(--ink-muted)] hover:text-red-700"
            >
              {del.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3" />
              )}{tr("Delete")}</button>
          </div>
        </div>
      </div>
    </li>
  );
}

export default function PromptHistoryPage() {
  const [filter, setFilter] = useState("all");
  const qc = useQueryClient();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["prompts", filter],
    queryFn: () => {
      // "failures" is client-side only — the backend has no such
      // prompt_type. Fetch everything and filter locally.
      if (filter === "all" || filter === "failures") {
        return api.listPrompts({ limit: 200 });
      }
      return api.listPrompts({ limit: 200, prompt_type: filter });
    },
    refetchOnMount: true,
  });

  useEffect(() => {
    // Light auto-refresh so newly-generated prompts appear without a
    // manual reload. 20s cadence is plenty for editorial workflows.
    const t = setInterval(() => {
      qc.invalidateQueries({ queryKey: ["prompts"] });
    }, 20000);
    return () => clearInterval(t);
  }, [qc]);

  const clearAll = useMutation({
    mutationFn: () => api.clearPrompts(),
    onSuccess: (r) => {
      toast.success(tr("Cleared {deleted} prompts", { deleted: r.deleted }));
      qc.invalidateQueries({ queryKey: ["prompts"] });
    },
  });

  const handleClear = () => {
    if (
      // eslint-disable-next-line no-alert
      window.confirm(
        tr("Delete every prompt from this property's history? This cannot be undone."),
      )
    ) {
      clearAll.mutate();
    }
  };

  const items = (data?.items || []).filter((it) =>
    filter === "failures" ? !!it.failure : true,
  );

  return (
    <div
      data-testid="prompt-history-page"
      className="flex-1 overflow-y-auto"
    >
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Terminal
                className="w-4 h-4 text-[var(--accent)]"
                strokeWidth={2}
              />
              {trRich("<0>Debug tool</0>", null, [<span className="overline" />])}
            </div>
            <h1 className="font-serif-display text-3xl leading-tight">{tr("Prompt history")}</h1>
            <p className="text-sm text-[var(--ink-muted)] mt-1 max-w-xl">{tr("Every prompt this newspaper has sent to the LLM. Copy any prompt to paste it into Claude Console or another tool for reverse-engineering & optimization. Failed generations also attach the model's raw response for post-mortem.")}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="prompt-history-refresh"
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
            >
              {isFetching ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : null}{tr("Refresh")}</button>
            <button
              data-testid="prompt-history-clear-all"
              onClick={handleClear}
              disabled={clearAll.isPending || items.length === 0}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-transparent hover:border-red-200 hover:bg-red-50 text-[var(--ink-muted)] hover:text-red-700 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <Trash2 className="w-3 h-3" />{tr("Clear all")}</button>
          </div>
        </div>

        {/* Type filter chips */}
        <div className="flex flex-wrap items-center gap-1.5 mb-5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`prompt-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={
                "text-xs px-2.5 py-1 border " +
                (filter === f.key
                  ? "border-[var(--slate-900)] bg-[var(--slate-900)] text-white"
                  : "border-[var(--border)] hover:border-[var(--ink)]")
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-[var(--ink-muted)]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />{tr("Loading prompt history…")}</div>
        ) : items.length === 0 ? (
          <div
            data-testid="prompt-history-empty"
            className="border border-dashed border-[var(--border)] p-10 text-center text-sm text-[var(--ink-muted)]"
          >{tr("No prompts yet. Generate a story idea, draft an article, or request an AI image — every LLM call will land here.")}</div>
        ) : (
          <ul
            data-testid="prompt-history-list"
            className="border border-[var(--border)]"
          >
            {items.map((it) => (
              <PromptRow key={it.id} item={it} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
